import { createSignal } from "solid-js";
import { accountIdForAlias } from "@/lib/account-alias";

export const TAB_ACCOUNT_STORAGE_KEY = "tab_account";
export const LAST_ACTIVE_ACCOUNT_KEY = "last_active_account";
export const TAB_ACCOUNT_CHANGED_EVENT = "space:tab-account-changed";
const ACCOUNTS_CHANGED_EVENT = "space:accounts-changed";
const ACCOUNTS_STORAGE_KEY = "accounts";
export const ACCOUNT_HANDOFF_PARAM = "as";

let handoffConsumed = false;

export function consumeAccountHandoff(): void {
  if (handoffConsumed) {
    return;
  }
  handoffConsumed = true;
  let alias: string | null = null;
  let url: URL;
  try {
    url = new URL(globalThis.location.href);
    alias = url.searchParams.get(ACCOUNT_HANDOFF_PARAM);
  } catch {
    return;
  }
  if (alias === null) {
    return;
  }
  const userId = accountIdForAlias(alias);
  if (userId !== null) {
    try {
      globalThis.sessionStorage.setItem(TAB_ACCOUNT_STORAGE_KEY, userId);
    } catch {
      // Storage unavailable
    }
    rememberLastActiveAccountId(userId);
  }
  try {
    url.searchParams.delete(ACCOUNT_HANDOFF_PARAM);
    globalThis.history.replaceState(globalThis.history.state, "", url.toString());
  } catch {
    // No History API
  }
  notifyTabAccountChanged();
}

export function readTabAccountId(): string | null {
  consumeAccountHandoff();
  try {
    const raw = globalThis.sessionStorage.getItem(TAB_ACCOUNT_STORAGE_KEY);
    return raw === null || raw === "" ? null : raw;
  } catch {
    return null;
  }
}

export function writeTabAccountId(userId: string, options?: { silent?: boolean }): void {
  try {
    globalThis.sessionStorage.setItem(TAB_ACCOUNT_STORAGE_KEY, userId);
  } catch {
    // Storage unavailable
  }
  rememberLastActiveAccountId(userId);
  if (options?.silent !== true) {
    notifyTabAccountChanged();
  }
}

export function clearTabAccountId(): void {
  try {
    globalThis.sessionStorage.removeItem(TAB_ACCOUNT_STORAGE_KEY);
  } catch {
    // Nothing to clear
  }
  notifyTabAccountChanged();
}

export function rememberLastActiveAccountId(userId: string): void {
  try {
    globalThis.localStorage.setItem(LAST_ACTIVE_ACCOUNT_KEY, userId);
  } catch {
    // Best-effort
  }
}

export function readLastActiveAccountId(): string | null {
  try {
    const raw = globalThis.localStorage.getItem(LAST_ACTIVE_ACCOUNT_KEY);
    return raw === null || raw === "" ? null : raw;
  } catch {
    return null;
  }
}

export function notifyTabAccountChanged(): void {
  try {
    globalThis.dispatchEvent(new Event(TAB_ACCOUNT_CHANGED_EVENT));
  } catch {
    // No DOM
  }
}

let revisionAccessor: (() => number) | null = null;

function trackTabAccountChanges(): number {
  if (!revisionAccessor) {
    const [revision, setRevision] = createSignal(0, { equals: false });
    const bump = () => {
      setRevision((r) => r + 1);
    };
    if (typeof globalThis.addEventListener === "function") {
      globalThis.addEventListener(TAB_ACCOUNT_CHANGED_EVENT, bump);
      globalThis.addEventListener(ACCOUNTS_CHANGED_EVENT, bump);
      globalThis.addEventListener("storage", (event) => {
        const { key } = event as StorageEvent;
        if (key === ACCOUNTS_STORAGE_KEY || key === null) {
          bump();
        }
      });
    }
    revisionAccessor = revision;
  }
  return revisionAccessor();
}

export function activeTabAccountId(): string | null {
  trackTabAccountChanges();
  return readTabAccountId();
}
